import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronRight, CheckCircle2 } from "lucide-react";
import { Overline } from "./primitives";
import { HeroDashboard } from "./HeroDashboard";

const trust = [
  "Factory Direct Purchasing",
  "Verified Turkish Manufacturers",
  "Full Container Orders",
  "One Workspace",
];

export const Hero = () => {
  const reduce = useReducedMotion();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 pb-16 sm:pt-36 lg:pb-24"
    >
      {/* soft ambient background */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-0 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-demax-soft/60 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.4]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #eef2f7 1px, transparent 1px), linear-gradient(to bottom, #eef2f7 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "radial-gradient(ellipse 70% 55% at 50% 0%, black 40%, transparent 75%)",
            WebkitMaskImage: "radial-gradient(ellipse 70% 55% at 50% 0%, black 40%, transparent 75%)",
          }}
        />
      </div>

      <div className="mx-auto max-w-7xl px-6 text-center lg:px-8">
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 shadow-card-base">
            <span className="h-1.5 w-1.5 rounded-full bg-demax" />
            <Overline>Workspace Product</Overline>
          </span>
        </motion.div>

        <motion.h1
          initial={reduce ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.06, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto mt-6 max-w-4xl font-display text-5xl font-extrabold leading-[1.05] tracking-tight text-ink md:text-6xl lg:text-[4.25rem]"
        >
          FullContainer™
        </motion.h1>

        <motion.p
          initial={reduce ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto mt-5 font-display text-2xl font-semibold tracking-tight text-ink md:text-3xl"
        >
          Full Container. One Manufacturer. One Workspace.
        </motion.p>

        <motion.p
          initial={reduce ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slateink md:text-lg"
        >
          Source full container quantities directly from verified Turkish manufacturers while
          managing quotations, purchase orders, logistics, inspections and shipment visibility
          inside one connected Workspace.
        </motion.p>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
          className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row"
        >
          <button
            data-testid="hero-primary-cta"
            className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-demax px-7 py-3.5 text-sm font-semibold text-white shadow-btn transition-all duration-200 hover:-translate-y-0.5 hover:bg-demax-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-demax focus-visible:ring-offset-2 sm:w-auto"
          >
            Create Your FullContainer™
            <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
          <button
            data-testid="hero-secondary-cta"
            className="inline-flex w-full items-center justify-center rounded-full border border-slate-200 bg-white px-7 py-3.5 text-sm font-semibold text-ink transition-all duration-200 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-demax focus-visible:ring-offset-2 sm:w-auto"
          >
            Create Your Free Workspace
          </button>
        </motion.div>

        <motion.div
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.34 }}
          className="mx-auto mt-9 flex max-w-3xl flex-wrap items-center justify-center gap-x-6 gap-y-2"
        >
          {trust.map((t) => (
            <span key={t} className="inline-flex items-center gap-2 text-[13px] font-medium text-slateink">
              <CheckCircle2 className="h-4 w-4 text-[#059669]" strokeWidth={2} />
              {t}
            </span>
          ))}
        </motion.div>
      </div>

      {/* dashboard */}
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto mt-16 max-w-5xl px-6 lg:px-8"
      >
        <HeroDashboard />
      </motion.div>
    </section>
  );
};
